import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import {
  NativeSessionSchema,
  clearSession,
  getAccessToken,
  isNativeApp,
  refreshTokenBody,
  saveSession,
} from "@/auth/nativeSession";
import { isNetworkError } from "@/offline/network";

// Dev sits behind IAP, which a native WebView can't complete interactively
// (same disallowed_useragent restriction that blocks Google Identity
// Services in-webview — see docs/mobile-app.md). This build-time token is
// IAP's own bearer-token bypass, the same mechanism already proven for
// Playwright (frontend/playwright.config.ts). It's a no-op whenever unset,
// so a real prod/dev build never carries it.
const iapHeaders = import.meta.env.VITE_IAP_BEARER_TOKEN
  ? { Authorization: `Bearer ${import.meta.env.VITE_IAP_BEARER_TOKEN}` }
  : undefined;

const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "/api",
  withCredentials: true,
  headers: iapHeaders,
});

// Plain instance with no interceptors, used only to clear a dead session —
// posting through `client` here would re-enter this same 401 handling.
const rawClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "/api",
  withCredentials: true,
  headers: iapHeaders,
});

// Native only (#225): mark the request so the backend answers with tokens
// instead of cookies, and authenticate with the in-memory access token.
function attachNativeAuth(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (!isNativeApp()) return config;
  config.headers.set("X-Client", "native");
  const accessToken = getAccessToken();
  if (accessToken) config.headers.set("X-Session-Token", accessToken);
  return config;
}

client.interceptors.request.use(attachNativeAuth);
rawClient.interceptors.request.use(attachNativeAuth);

// Native only: keep any session the backend hands back (sign-in, refresh,
// password change) before the caller — or a queued retry — sees the response.
client.interceptors.response.use(async (res: AxiosResponse) => {
  if (!isNativeApp()) return res;
  const session = NativeSessionSchema.safeParse(res.data);
  if (session.success) await saveSession(session.data);
  return res;
});

// Every route in App.tsx that isn't behind ProtectedRoute. AuthProvider calls
// /users/me on every page load, so a signed-out visitor on a page missing
// here gets bounced to /login.
const PUBLIC_PATHS = [
  "/",
  "/login",
  "/register",
  "/complete-google-signup",
  "/link-google-account",
  "/verify",
  "/confirm-email-change",
  "/reset-password",
  "/forgot-password",
  "/privacy",
  "/terms",
  "/support",
  "/delete-account",
];

// These return 401 as a normal "bad credentials" response, not "your access
// token expired" — attempting a refresh here would just replace a meaningful
// error (e.g. "Incorrect password") with a useless "No refresh token" one,
// since the caller was never logged in to begin with. Logout is here too:
// refreshing a session only to revoke it is pointless (#242).
const NO_REFRESH_RETRY_PATHS = ["/auth/login", "/auth/google", "/auth/google/link", "/auth/logout"];

let isRefreshing = false;
let pendingQueue: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];
let loggingOut = false;

function drainQueue(err: unknown) {
  pendingQueue.forEach((p) => (err ? p.reject(err) : p.resolve()));
  pendingQueue = [];
}

// "/" must match exactly: as a prefix it would match every page.
function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((p) => (p === "/" ? pathname === "/" : pathname.startsWith(p)));
}

function redirectToLogin() {
  if (isPublicPath(window.location.pathname) || loggingOut) return;
  loggingOut = true;

  // The refresh already failed, so this cookie is dead — clear it server-side
  // too. Otherwise the browser keeps presenting the same invalid cookie on
  // every future visit and this exact failure repeats indefinitely.
  refreshTokenBody()
    .then((body) => rawClient.post("/auth/logout", body))
    .catch(() => {})
    .then(() => clearSession())
    .catch(() => {})
    .finally(() => {
      window.location.href = "/login";
    });
}

client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    // Refresh endpoint itself 401'd — session is fully expired
    if (original.url === "/auth/refresh") {
      redirectToLogin();
      return Promise.reject(error);
    }

    if (NO_REFRESH_RETRY_PATHS.includes(original.url)) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: () => resolve(client(original)),
          reject,
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      await client.post("/auth/refresh", await refreshTokenBody());
      drainQueue(null);
      return client(original);
    } catch (refreshErr) {
      drainQueue(refreshErr);
      // Offline isn't signed out: ending the session here would also wipe the
      // native app's stored refresh token, losing it at the crag/gym.
      if (!isNetworkError(refreshErr)) redirectToLogin();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default client;
